import React, { useEffect, useState } from "react";
import { Highlighter, StickyNote, Plus, Copy, X, Trash2, Search } from "lucide-react";
import { HIGHLIGHT_COLORS } from "@/components/Reader";
import { createHighlight, deleteHighlights, buildPassage, listNotes, updateNote, getNote } from "@/lib/api";

const PALETTE = [
    { name: "sage", value: "#B7CDB1" },
    { name: "amber", value: "#E7C36F" },
    { name: "plum", value: "#C7A0B0" },
    { name: "slate", value: "#9CB0C0" },
    { name: "clay", value: "#D8B49E" },
];

export default function SelectionActionBar({
    chapter,
    selectedVerses,
    hasHighlight,
    version,
    onDone,
    onNavigateNote,
    onToast,
}) {
    const [menu, setMenu] = useState(null); // "highlight" | "add" | null
    const refs = selectedVerses.map(v => `Mat.${chapter}.${v}`);
    const label = buildLabel(chapter, selectedVerses);

    const doHighlight = async (color) => {
        await createHighlight(refs, color);
        onToast?.("Highlighted");
        setMenu(null);
        onDone?.({ reload: true });
    };
    const doRemoveHighlight = async () => {
        await deleteHighlights(refs);
        onToast?.("Highlight removed");
        onDone?.({ reload: true });
    };
    const doCopy = async () => {
        try {
            const p = await buildPassage(refs, version);
            const payload = `${p.text}\n— ${p.label} (${(p.translation || "kjv").toUpperCase()})`;
            let ok = false;
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(payload);
                    ok = true;
                }
            } catch (_e) { /* fall through */ }
            if (!ok) {
                // execCommand fallback for non-secure/automation contexts
                const ta = document.createElement("textarea");
                ta.value = payload;
                ta.style.position = "fixed"; ta.style.left = "-1000px";
                document.body.appendChild(ta);
                ta.select();
                try { ok = document.execCommand("copy"); } catch { ok = false; }
                document.body.removeChild(ta);
            }
            onToast?.(ok ? "Copied" : "Copy failed");
        } catch (_e) { onToast?.("Copy failed"); }
        onDone?.();
    };
    const doNewNote = async (format) => {
        const p = await buildPassage(refs, version);
        // Persist selection to sessionStorage; composer reads it
        sessionStorage.setItem("pending-passage-card", JSON.stringify({
            refs: p.refs, label: p.label, translation: p.translation,
            text: p.text, format,
        }));
        onNavigateNote?.("/notes/new?fromPassage=1");
    };

    return (
        <div data-testid="selection-bar" className="fixed inset-x-0 bottom-0 z-40 pointer-events-none">
            <div className="max-w-md md:max-w-2xl mx-auto p-3 pointer-events-auto">
                {menu === "highlight" && (
                    <div className="mb-2 p-3 rounded-2xl bg-white shadow-xl border border-ink-10 flex items-center gap-2">
                        <span className="font-ui text-[11px] uppercase tracking-[0.18em] text-ink-40 mr-1">Highlight</span>
                        {PALETTE.map(c => (
                            <button
                                key={c.name}
                                data-testid={`highlight-color-${c.name}`}
                                onClick={() => doHighlight(c.name)}
                                className="w-9 h-9 rounded-full ring-1 ring-ink-10 hover:ring-ink-40 transition"
                                style={{ background: c.value }}
                                aria-label={c.name}
                            />
                        ))}
                        {hasHighlight && (
                            <button
                                data-testid="remove-highlight"
                                onClick={doRemoveHighlight}
                                className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-ui text-[12px] bg-ink/5 text-ink hover:bg-ink/10 transition"
                            >
                                <Trash2 size={14} /> Remove
                            </button>
                        )}
                        <button onClick={() => setMenu(null)} className="ml-auto w-8 h-8 rounded-full flex items-center justify-center text-ink-40 hover:bg-ink-10">
                            <X size={16} />
                        </button>
                    </div>
                )}
                {menu === "add" && (
                    <AddToNotePicker
                        refs={refs}
                        version={version}
                        label={label}
                        onClose={() => setMenu(null)}
                        onDone={(noteId) => { onToast?.("Added to note"); onNavigateNote?.(`/notes/${noteId}/edit`); }}
                    />
                )}
                {menu === "newFormat" && (
                    <div className="mb-2 p-3 rounded-2xl bg-white shadow-xl border border-ink-10 flex items-center gap-2">
                        <span className="font-ui text-[11px] uppercase tracking-[0.18em] text-ink-40 mr-1">Insert as</span>
                        <button data-testid="format-full" onClick={() => doNewNote("full")}
                            className="px-3 py-1.5 rounded-full bg-moss text-white font-ui text-[12px]">Full text</button>
                        <button data-testid="format-ref" onClick={() => doNewNote("ref")}
                            className="px-3 py-1.5 rounded-full bg-white border border-ink-10 font-ui text-[12px] text-ink hover:border-moss/40">Reference only</button>
                        <button onClick={() => setMenu(null)} className="ml-auto w-8 h-8 rounded-full flex items-center justify-center text-ink-40 hover:bg-ink-10">
                            <X size={16} />
                        </button>
                    </div>
                )}

                <div className="p-3 rounded-2xl bg-white shadow-xl border border-ink-10 flex items-center gap-2">
                    <div className="font-ui text-[12px] text-ink-70 flex-1 truncate">
                        <span data-testid="selection-label" className="font-semibold text-ink">{label}</span>
                        <span className="ml-1 text-ink-40">selected</span>
                    </div>
                    <ActionBtn testid="action-highlight" icon={Highlighter} label="Highlight" onClick={() => setMenu(m => m === "highlight" ? null : "highlight")} />
                    <ActionBtn testid="action-new-note" icon={Plus} label="New note" onClick={() => setMenu("newFormat")} />
                    <ActionBtn testid="action-add-note" icon={StickyNote} label="Add" onClick={() => setMenu("add")} />
                    <ActionBtn testid="action-copy" icon={Copy} label="Copy" onClick={doCopy} />
                    <button
                        data-testid="close-selection"
                        onClick={() => onDone?.()}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-ink-70 hover:bg-ink-10 transition"
                        aria-label="Cancel selection"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}

function ActionBtn({ testid, icon: Icon, label, onClick }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className="inline-flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg font-ui text-[10px] text-ink-70 hover:text-moss-deep hover:bg-moss/10 transition min-w-[52px]"
        >
            <Icon size={16} />
            <span>{label}</span>
        </button>
    );
}

function AddToNotePicker({ refs, version, label, onClose, onDone }) {
    const [q, setQ] = useState("");
    const [notes, setNotes] = useState([]);
    useEffect(() => {
        listNotes({ q: q.trim() || undefined }).then(d => setNotes(d.notes || []));
    }, [q]);

    const append = async (noteId) => {
        const p = await buildPassage(refs, version);
        const doc = (await getNote(noteId)).note;
        const newContent = [...(doc.content || [])];
        const uid = `n_${Math.random().toString(36).slice(2, 10)}`;
        newContent.push(
            { type: "text", text: newContent.length ? "\n" : "" },
            { type: "verse_card", node_id: uid, reference: p.refs[0], refs: p.refs, label: p.label, translation: p.translation, text: p.text, format: "full" },
        );
        await updateNote(noteId, { content: newContent });
        // Persist a pending toast that survives the navigation
        sessionStorage.setItem("pending-toast", "Added to note");
        onDone?.(noteId);
    };

    return (
        <div className="mb-2 rounded-2xl bg-white shadow-xl border border-ink-10 overflow-hidden">
            <div className="p-3 border-b border-ink-10 flex items-center gap-2">
                <Search size={14} className="text-ink-40" />
                <input
                    autoFocus
                    data-testid="add-note-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search notes…"
                    className="flex-1 bg-transparent outline-none font-ui text-[13px] text-ink placeholder:text-ink-40"
                />
                <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-ink-40 hover:bg-ink-10">
                    <X size={16} />
                </button>
            </div>
            <div className="max-h-64 overflow-y-auto sheet-scroll">
                {notes.length === 0 ? (
                    <p className="p-4 font-ui text-sm text-ink-40">No notes yet.</p>
                ) : (
                    <ul>
                        {notes.map(n => (
                            <li key={n.id}>
                                <button
                                    data-testid={`add-note-target-${n.id}`}
                                    onClick={() => append(n.id)}
                                    className="w-full text-left px-4 py-2.5 hover:bg-sage-paper transition"
                                >
                                    <div className="scripture text-[14px] text-ink">
                                        {n.title || <span className="text-ink-40 italic">Untitled</span>}
                                    </div>
                                    {n.excerpt && (
                                        <div className="mt-0.5 font-ui text-[11px] text-ink-40 line-clamp-1">{n.excerpt}</div>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function buildLabel(ch, verses) {
    const vs = [...verses].sort((a, b) => a - b);
    if (vs.length === 0) return "";
    // Collapse consecutive runs: [1,2,3,4,7,8,10] → "1-4, 7-8, 10"
    const runs = [];
    let cur = [vs[0]];
    for (let i = 1; i < vs.length; i++) {
        if (vs[i] === cur[cur.length - 1] + 1) cur.push(vs[i]);
        else { runs.push(cur); cur = [vs[i]]; }
    }
    runs.push(cur);
    const pieces = runs.map(r => r.length === 1 ? String(r[0]) : `${r[0]}-${r[r.length - 1]}`);
    const suffix = vs.length > 1 ? ` (${vs.length})` : "";
    return `Matthew ${ch}:${pieces.join(", ")}${suffix}`;
}
